<template>
  <div class="visualization-controls">
    <b-row>
      <b-col v-if="showColor" cols="2">
        <label class="mr-1">Color</label>
        <b-form-select
          v-model="localColor"
          :options="visualization.getSeparatedColorOptions()"
          size="sm"
        ></b-form-select>
      </b-col>

      <b-col v-if="showMetric" cols="2">
        <label class="mr-1">Metric</label>
        <b-form-select
          v-model="localMetric"
          :options="visualization.getSeparatedMetricOptions()"
          size="sm"
        ></b-form-select>
      </b-col>

      <b-col v-if="showMetricX" cols="2">
        <label class="mr-1">Metric X</label>
        <b-form-select
          v-model="localMetricX"
          :options="visualization.getSeparatedMetricOptions()"
          size="sm"
        ></b-form-select>
      </b-col>

      <b-col v-if="showMetricY" cols="2">
        <label class="mr-1">Metric Y</label>
        <b-form-select
          v-model="localMetricY"
          :options="visualization.getSeparatedMetricOptions()"
          size="sm"
        ></b-form-select>
      </b-col>

      <b-col v-if="showSortX || showSortXDirectionOnly" cols="2">
        <label class="mr-1">{{ xLabel() }}</label>
        <b-row col="8" no-gutters>
          <b-col v-if="showSortX">
            <b-form-select
              v-model="localSortX"
              :options="visualization.sortXYOptions"
              size="sm"
            ></b-form-select>
          </b-col>
          <b-col cols="3">
            <b-btn
              class="ml-1"
              variant="outline-secondary"
              block
              size="sm"
              :disabled="
                localSortX === 'clustering' || localSortX === 'clustering_docs'
              "
              @click="switchSortXDirection"
            >
              <i
                v-if="localSortXDirection === 'ascending'"
                class="fa fa-sort-amount-down"
              />
              <i v-else class="fa fa-sort-amount-down-alt" />
            </b-btn>
          </b-col>
        </b-row>
      </b-col>

      <b-col v-if="showSortY" cols="2">
        <label class="mr-1">Sort Y</label>
        <b-row col="8" no-gutters>
          <b-col>
            <b-form-select
              v-model="localSortY"
              :options="visualization.sortXYOptions"
              size="sm"
            ></b-form-select>
          </b-col>
          <b-col cols="3">
            <b-btn
              class="ml-1"
              variant="outline-secondary"
              block
              size="sm"
              :disabled="
                localSortY === 'clustering' || localSortY === 'clustering_docs'
              "
              @click="switchSortYDirection"
            >
              <i
                v-if="localSortYDirection === 'ascending'"
                class="fa fa-sort-amount-down"
              />
              <i v-else class="fa fa-sort-amount-down-alt" />
            </b-btn>
          </b-col>
        </b-row>
      </b-col>

      <b-col v-if="showSortZ" cols="2">
        <label class="mr-1">Sort Z</label>
        <b-form-select
          v-model="localSortZ"
          :options="visualization.getSortZOptions()"
          size="sm"
        ></b-form-select>
      </b-col>

      <b-col v-if="showRecency" cols="2">
        <b-form-group
          id="input-group-recency"
          label-for="input-recency"
          class="year-slider"
        >
          <Slider
            v-if="visualization.recencyLimit && visualization.recencyRange"
            v-model="localRecencyLimit"
            :decimals="2"
            :min="visualization.recencyRange[0]"
            :max="visualization.recencyRange[1]"
            :type="'recency'"
          ></Slider>
        </b-form-group>
      </b-col>

      <b-col v-if="showSize" cols="2">
        <b-form-group
          id="input-group-size"
          label-for="input-size"
          class="year-slider"
        >
          <Slider
            v-if="visualization.sizeLimit && visualization.sizeRange"
            v-model="localSizeLimit"
            :min="visualization.sizeRange[0]"
            :max="visualization.sizeRange[1]"
            :type="'size'"
          ></Slider>
        </b-form-group>
      </b-col>
      
      <b-col v-if="showNormalizeX" cols="2">
        <b-form-checkbox
          v-model="localNormalizeX"
          switch
          size="sm"
          class="mt-4"
        >
          Normalize over X
        </b-form-checkbox>
      </b-col>
      <!-- TODO: Support scatter plot types & categories -->
    </b-row>
  </div>
</template>

<script>
import Slider from "@/components/Explore/Shared/Slider";

export default {
  name: "VisualizationControls",
  components: {
    Slider
  },
  props: {
    visualization: {
      type: Object,
      required: true
    },
    showColor: {
      type: Boolean,
      required: false
    },
    showMetric: {
      type: Boolean,
      required: false
    },
    showMetricX: {
      type: Boolean,
      required: false
    },
    showMetricY: {
      type: Boolean,
      required: false
    },
    showSortX: {
      type: Boolean,
      required: false
    },
    showSortXDirectionOnly: {
      type: Boolean,
      required: false
    },
    showSortY: {
      type: Boolean,
      required: false
    },
    showSortZ: {
      type: Boolean,
      required: false
    },
    showRecency: {
      type: Boolean,
      required: false
    },
    showSize: {
      type: Boolean,
      required: false
    },
    showNormalizeX: {
      type: Boolean,
      required: false
    }
  },
  data() {
    return {};
  },
  computed: {
    localColor: {
      get() {
        return this.visualization.color;
      },
      set(val) {
        this.visualization.setColor(val);
      }
    },
    localMetric: {
      get() {
        return this.visualization.metric;
      },
      set(val) {
        this.visualization.setMetric(val);
      }
    },
    localMetricX: {
      get() {
        return this.visualization.metricX;
      },
      set(val) {
        this.visualization.setMetricX(val);
      }
    },
    localMetricY: {
      get() {
        return this.visualization.metricY;
      },
      set(val) {
        this.visualization.setMetricY(val);
      }
    },
    localSortX: {
      get() {
        let val = this.visualization.sortX;
        if (val.includes("_des")) {
          val = val.replace("_des", "");
        }
        return val;
      },
      set(val) {
        if (
          this.localSortXDirection === "descending" &&
          ["normal", "size", "filtsize", "recency", "filtrecency"].includes(val)
        ) {
          val += "_des";
        }
        this.visualization.setSortX(val);
      }
    },
    localSortXDirection: {
      get() {
        return this.visualization.sortX.includes("_des")
          ? "descending"
          : "ascending";
      },
      set(val) {
        if (
          val === "descending" &&
          ["normal", "size", "filtsize", "recency", "filtrecency"].includes(this.localSortX)
        ) {
          this.visualization.setSortX(this.localSortX + "_des");
        } else {
          this.visualization.setSortX(this.localSortX);
        }
      }
    },
    localSortY: {
      get() {
        let val = this.visualization.sortY;
        if (val.includes("_des")) {
          val = val.replace("_des", "");
        }
        return val;
      },
      set(val) {
        if (
          this.localSortYDirection === "descending" &&
          ["normal", "size", "filtsize", "recency", "filtrecency"].includes(val)
        ) {
          val += "_des";
        }
        this.visualization.setSortY(val);
      }
    },
    localSortZ: {
      get() {
        return this.visualization.sortZ;
      },
      set(val) {
        this.visualization.setSortZ(val);
      }
    },
    localSortYDirection: {
      get() {
        return this.visualization.sortY.includes("_des")
          ? "descending"
          : "ascending";
      },
      set(val) {
        if (
          val === "descending" &&
          ["normal", "size", "filtsize", "recency", "filtrecency"].includes(this.localSortY)
        ) {
          this.visualization.setSortY(this.localSortY + "_des");
        } else {
          this.visualization.setSortY(this.localSortY);
        }
      }
    },
    localRecencyLimit: {
      get() {
        return this.visualization.recencyLimit;
      },
      set(val) {
        this.visualization.setRecencyLimit(val);
      }
    },
    localSizeLimit: {
      get() {
        return this.visualization.sizeLimit;
      },
      set(val) {
        this.visualization.setSizeLimit(val);
      }
    },
    localNormalizeX: {
      get() {
        return this.visualization.normalizeOverX;
      },
      set(val) {
        this.visualization.setNormalizeOverX(val);
      }
    }
  },
  methods: {
    switchSortXDirection() {
      if (this.localSortXDirection === "ascending") {
        this.localSortXDirection = "descending";
      } else {
        this.localSortXDirection = "ascending";
      }
    },
    switchSortYDirection() {
      if (this.localSortYDirection === "ascending") {
        this.localSortYDirection = "descending";
      } else {
        this.localSortYDirection = "ascending";
      }
    },
    xLabel(){
      if(['columnchart','heatmap','linechart'].includes(this.visualization.type)){
        return 'Sort X'
      }
      else if(this.visualization.type === 'barchart'){
        return 'Sort Y'
      }
      else{
        return 'Sort'
      }
    },
  }
};
</script>

<style lang="scss">
.visualization-controls {
  margin-bottom: 50px;
}
</style>
